import { Skeleton } from '@app/components/various/Skeleton';
import { cn } from '@app/utils/cn';
import React from 'react';

interface Props<T extends string = string> {
  isSelected?: boolean;
  onChange?: (value: React.ChangeEvent<HTMLInputElement>) => void;
  children:
    | React.ReactNode
    | ((props: { isSelected: boolean; isDisabled: boolean; inputId: string; describedById: string }) => React.ReactNode);
  name?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  value: T;
  selectedValue?: T;
  className?: string;
  hasDescription?: boolean;
  errorMessageId?: string;
}

export const RadioCard: React.FC<Props> = ({
  selectedValue,
  onChange,
  name,
  value,
  children,
  isDisabled = false,
  isLoading,
  hasDescription = false,
  errorMessageId,
  className,
}) => {
  const isSelected = selectedValue === value;
  const inputId = React.useId();
  const describedById = React.useId();

  const ariaDescribedById = errorMessageId ?? (hasDescription ? describedById : undefined);

  return (
    <div className={cn('relative cursor-pointer overflow-hidden rounded-regular has-[:focus-visible]:focus-outline', className)}>
      <input
        id={inputId}
        aria-describedby={ariaDescribedById}
        type="radio"
        value={value}
        checked={value === selectedValue}
        onChange={onChange}
        name={name}
        className="sr-only"
        disabled={isDisabled || isLoading}
      />
      <div
        className={cn('h-full rounded-regular border p-5 transition-colors', {
          'border-neutral-90': !isSelected,
          'border-transparent bg-neutral-96/85': isSelected,
        })}
      >
        {isLoading && <Skeleton type="rectangle" className="absolute inset-0 z-elevated h-full w-full" />}
        {!isLoading && isSelected && <div className="absolute -left-2 -right-2 top-0 h-0.5 bg-neutral-20"></div>}
        {typeof children === 'function' ? children({ describedById, inputId, isDisabled, isSelected }) : children}
      </div>
    </div>
  );
};
